import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import TextArea from "../../components/TextArea/TextArea";
import Button from "../../components/Button/Button";
import LeftPanel from "../../components/LeftPanel/LeftPanel";
import OnboardingStepHeader from "../../components/OnboardingStepHeader/OnboardingStepHeader";
import { saveStep2Data, goToNextStep, goToPreviousStep, setStep2Data } from "../../redux/slices/OfficeOnboardingSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { OfficeService } from "../../services/office.service";
import { logger } from "../../utils/logger";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { ToastVariants } from "../../models";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";

const log = logger.getLogger("OnboardingStep2");

interface OfficeData {
  name: string;
  address: string;
}

const Step2: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useContext(ToastContext);
  const { step2Data, isLoading } = useSelector((state: RootState) => state.officeOnboarding);
  const { currentUser } = useSelector((state: RootState) => state.currentUser);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const officeId = currentUser?.offices?.[0]?.officeId;

  const [letterheadText, setLetterheadText] = useState(step2Data?.letterheadAdditionalText || "");
  const [error, setError] = useState("");
  const [officeData, setOfficeData] = useState<OfficeData | null>(null);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [includeAddress, setIncludeAddress] = useState(false);
  const maxLength = 250;

  // Fetch office data for letterhead preview and prefill
  useEffect(() => {
    const fetchOfficeData = async () => {
      if (!officeId) return;

      try {
        const office = await OfficeService.getOfficeV2(officeId);
        if (office) {
          const name = office.name || "";
          const address = [office?.street, office?.street1, office?.city, office?.state, office?.billingCountry].filter(Boolean).join(", ") || "";
          setOfficeData({ name, address });
          setIncludeAddress(office?.letterHeader?.isLetterHeaderAddress);
          // Prefill letterhead text if saved in backend; strip office name (and address if included) so input shows only additional text
          if (office?.letterHeader?.letterHeader && !step2Data?.letterheadAdditionalText) {
            let saved = office.letterHeader.letterHeader;
            const prefix = office.letterHeader.isLetterHeaderAddress ? [name, address].filter(Boolean).join("\n") : name;
            if (prefix && (saved === prefix || saved.startsWith(prefix + "\n"))) {
              saved = saved.slice(prefix.length).replace(/^\n+/, "");
            }
            setLetterheadText(saved);
          }
        }
      } catch (err) {
        log.error("Failed to fetch office data for letterhead preview");
      }
    };

    fetchOfficeData();
  }, [officeId, step2Data?.letterheadAdditionalText]);

  useEffect(() => {
    if (step2Data?.letterheadAdditionalText) {
      setLetterheadText(step2Data.letterheadAdditionalText);
    }
  }, [step2Data]);

  const validateLetterhead = (value: string): string => {
    // Field is required - check if empty or contains only whitespace
    if (!value || value.trim().length === 0) {
      return "This field is required.";
    }

    // Check if value exceeds maximum length
    if (value.length > maxLength) {
      return "You have exceeded the 250-character limit.";
    }

    // Validate allowed characters: alphanumeric, spaces, line breaks, and basic punctuation (.,-&()/#@: etc.)
    // Allow: letters, numbers, spaces, line breaks (\n), and: . , - & ( ) / # @ :
    const allowedCharsRegex = /^[a-zA-Z0-9\s\n.,\-&()/#@:]*$/;
    if (!allowedCharsRegex.test(value)) {
      return "Invalid characters. Only alphanumeric characters, spaces, line breaks, and basic punctuation (.,-&()/#@:) are allowed.";
    }

    return "";
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;

    // Allow input up to maxLength, but show error if exceeded
    if (value.length <= maxLength) {
      setLetterheadText(value);
      const errorMsg = validateLetterhead(value);
      setError(errorMsg);
    } else {
      // If exceeding maxLength, still update but show error
      setLetterheadText(value);
      setError("You have exceeded the 250-character limit.");
    }
  };

  const handleBlur = () => {
    // Validate on blur to provide immediate feedback when user leaves the field
    const errorMsg = validateLetterhead(letterheadText);
    setError(errorMsg);
  };

  const handleBack = () => {
    dispatch(goToPreviousStep());
  };

  const handleSubmit = async () => {
    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }
    // Frontend validation first
    const validationError = validateLetterhead(letterheadText);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Clear error if validation passes
    setError("");

    if (!officeId) {
      toast?.openToast("Office not found. Please try again.", 3000, ToastVariants.Error);
      return;
    }
    setIsSubmitting(true);
    try {
      // Always: Office Name + Input Text. When checkbox checked: Office Name + Address + Input Text (with line breaks)
      const addressPart = includeAddress && officeData?.address ? officeData.address : null;
      const letterHeaderToSend = [officeData?.name, addressPart, letterheadText].filter(Boolean).join("\n");

      const datatoSend = {
        letterHeader: letterHeaderToSend,
        isLetterHeaderAddress: includeAddress,
      };
      // Save letterhead to backend
      const success = await OfficeService.updateLetterHeader(officeId, datatoSend);

      if (!success) {
        toast?.openToast("Failed to save letterhead. Please try again.", 3000, ToastVariants.Error);
        return;
      }

      // Save to local state for step tracking
      await dispatch(
        saveStep2Data({
          data: {
            letterheadAdditionalText: letterheadText,
          },
          officeId: officeId,
        })
      ).unwrap();

      dispatch(setStep2Data({ letterheadAdditionalText: letterheadText }));
      dispatch(goToNextStep());
    } catch (err) {
      toast?.openToast("Failed to save. Please try again.", 3000, ToastVariants.Error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVideoPlay = () => {
    log.debug("Video play clicked");
  };

  const currentDate = new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });

  return (
    <div className="flex h-full min-h-0">
      {/* Left Panel */}
      <LeftPanel onVideoPlay={handleVideoPlay} />

      {/* Right Panel */}
      <div className="w-full md:w-2/3 bg-white flex flex-col h-full min-h-0">
        {/* Sticky Header with Progress */}
        <div className="sticky top-0 z-10 bg-white flex-shrink-0">
          <OnboardingStepHeader currentStep={2} totalSteps={5} />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="flex justify-center">
            <div className="p-8 w-full max-w-4xl">
              <div className="space-y-6">
                <div>
                  <TextArea
                    label="Include all additional letterhead information as required, this is mandatory"
                    placeholder="e.g., medical practice number, phone number, fax"
                    value={letterheadText}
                    onChange={handleTextChange}
                    onBlur={handleBlur}
                    name="letterheadText"
                    isRequired={true}
                    extraTextAreaClass=" min-h-[150px] resize-none border-none bg-gray-100"
                  />
                  <div className="mt-1.5 flex justify-end">
                    <span className="text-sm text-gray-500">
                      {letterheadText?.length || 0}/{maxLength}
                    </span>
                  </div>
                  {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
                </div>

                {/* checkbox for add address in letterhead */}
                <div className="flex items-center pt-2">
                  <input
                    id="includeAddress"
                    type="checkbox"
                    checked={includeAddress}
                    onChange={e => setIncludeAddress(e.target.checked)}
                    className="h-4 w-4 text-docsigna-blue-dark border-gray-300 rounded"
                  />
                  <label htmlFor="includeAddress" className="ml-2 text-sm text-gray-700">
                    Include office address in letterhead
                  </label>
                </div>

                {/* Preview Section */}
                <div className="mt-6 bg-gray-100 p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-medium text-docsigna-blue-dark tracking-[-0.31px]">Letterhead Preview</h3>
                    <button
                      onClick={() => setShowFullPreview(true)}
                      className="flex items-center gap-1 text-docnote-orange hover:text-orange-600 transition-colors"
                    >
                      <DescriptionOutlinedIcon className="!text-lg" />
                      <span className="text-sm font-medium tracking-[-0.15px] underline">Preview Full Letterhead</span>
                      <ArrowForwardIcon className="!text-lg" />
                    </button>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-6 bg-white max-h-40 overflow-y-auto">
                    <p className="font-semibold text-docsigna-blue-dark">{officeData?.name || "Office Name"}</p>
                    {includeAddress && <p className=" text-gray-600">{officeData?.address || "Office address"}</p>}
                    {letterheadText && <p className="text-gray-600 whitespace-pre-line">{letterheadText}</p>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Footer with Navigation Buttons */}
        <div className="sticky bottom-0 z-10 bg-white flex-shrink-0 p-8">
          <div className="flex justify-between">
            <Button text="Back" onClick={handleBack} variant="Tertiary" width="fit" className="px-8" buttonStyle="square" />
            <Button
              text="Save & Continue"
              onClick={handleSubmit}
              disabled={isLoading || !!error || isSubmitting}
              variant={isLoading || isSubmitting ? "loading" : "Primary"}
              width="fit"
              className="px-8"
              buttonStyle="square"
            />
          </div>
        </div>
      </div>

      {/* Full Letterhead Preview Modal */}
      {showFullPreview && (
        <div className="fixed inset-0 z-[100] bg-black bg-opacity-50 flex items-center justify-center p-4" onClick={() => setShowFullPreview(false)}>
          <div className="relative bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            {/* Close Button */}
            <button onClick={() => setShowFullPreview(false)} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 z-10">
              <CloseIcon />
            </button>

            {/* Letterhead Document */}
            <div className="p-8">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-docsigna-blue-dark">Letterhead Preview</h3>
              </div>
              <div className="border border-gray-300 rounded-lg p-8 bg-white shadow-sm min-h-[700px] flex flex-col">
                {/* Header */}
                <div className="text-center mb-8">
                  <p className="font-semibold text-gray-700 text-lg">{officeData?.name}</p>
                  {includeAddress && <p className="text-gray-600 text-[16px]">{officeData?.address || "Office address"}</p>}
                  {letterheadText && <p className="text-gray-600 text-[16px] whitespace-pre-line">{letterheadText}</p>}
                </div>
                <div className="border-t border-gray-300 pt-6">
                  <div className="text-sm text-gray-600 mb-4">
                    <strong>Date:</strong> {new Date().toLocaleDateString()}
                  </div>
                  <div className="text-sm text-gray-600 mb-4">
                    <strong>To:</strong> [Recipient Name]
                    <br />
                    [Recipient Address]
                    <br />
                    [City, Province Postal Code]
                  </div>
                  <div className="text-sm text-gray-600 mb-4">
                    <strong>Subject:</strong> [Document Subject]
                  </div>
                  <div className="text-sm text-gray-600 leading-relaxed">
                    <p className="mb-4">Dear [Recipient Name],</p>
                    <p className="mb-4">
                      This is a sample document to demonstrate how your letterhead will appear on official documents. The content above represents
                      your letterhead information that will be displayed at the top of all generated documents.
                    </p>
                    <p className="mb-4">You can customize this letterhead content anytime by editing the text in the input field above.</p>
                    <p className="mb-4">
                      Sincerely,
                      <br />
                      [Your Name]
                      <br />
                      [Your Title]
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step2;
